import PropTypes from 'prop-types';
import Row from 'react-bootstrap/Row';
import parse from 'html-react-parser';
import { Fragment } from 'react';

import ButtonDisclosures from '../../../atoms/button-disclosures';
import ButtonSwitch from '../../../atoms/button-switch';
import CardModule from '../../shared/card-module';
import ChartContainer from '../../../atoms/chart-container';
import Col from 'react-bootstrap/Col';
import DISCLOSURES from '../../../../helpers/statics/performance-returns-disclosures';
import Label from '../../../atoms/label';
import TableBasic from '../../../atoms/table-basic/index';
import format from '../../../../utils/format';
import styles from './performance-returns.module.scss';
import usePerformanceReturnsStore from '../../../../stores/profile/modules/use-performance-returns';
import { FUND_TYPES } from '../../../../stores/profile/get-fund-type';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { getTextValueColorClass } from '../../../../utils/sharedHelpers';

const className = augmentClassName(styles);

const labelColorByIndex = {
  0: 'chartBlue',
  1: 'chartViolet',
  2: 'chartGreen',
  3: 'chartPurple'
};

const PerformanceReturns = ({
  isAlternateInceptionDate,
  showBenchmarkAndAverage,
  isRetirePlus,
  suppressColumns,
  isPropFund
}) => {
  const {
    // TODO: replace fund data with the individual properties that the component needs
    fundData = {},
    onChangePeriod,
    onResizeChart,
    periods,
    periodSelected,
    performanceDisclosure,
    performanceAsOfDate,
    showComparativeBenchmarkFootnote,
    showExpenseRatio,
    showPortfolioInceptionDate,
    showUnderlyingFundInceptionDate,
    src,
    supplementalData = [],
    underlyingFundInceptionDate,
    fundType,
    investmentFacts
  } = usePerformanceReturnsStore(isAlternateInceptionDate, showBenchmarkAndAverage);

  const moduleLabel = isRetirePlus ? 'Quarterly Performance Returns' : 'Performance Returns';

  const renderRowLabel = (rowData, index) => {
    const isCategoryAverage =
      rowData.Label.includes('Morningstar') && rowData.Label.includes('Average');
    const isMaxSalesCharge = rowData.Label.includes('Max') && rowData.Label.includes('Charge');
    if (rowData.VehicleType === 'Fund') {
      return (
        <Label
          labelColor={labelColorByIndex[index]}
          labelText="This Investment"
          // tooltip="To be determined by TIAA"
        />
      );
    }
    if (rowData.VehicleType === 'Morningstar Category' || isCategoryAverage) {
      return (
        <Label
          labelColor={labelColorByIndex[index]}
          labelText="Category Avg."
          // tooltip="To be determined by TIAA"
        />
      );
    }
    if (isMaxSalesCharge) {
      return (
        <Label
          labelColor={labelColorByIndex[index]}
          labelText="Sales Charge"
          // tooltip="To be determined by TIAA"
        />
      );
    }
    return (
      <Label
        labelColor={labelColorByIndex[index]}
        labelText="Benchmark"
        // tooltip="To be determined by TIAA"
      />
    );
  };

  const getShowSuperscript = ({ inceptionDate, asOfDate, cellIndex, shareClass }) => {
    let showSuperscript = false;
    const IsLinkedPerf1Mth =
      new Date(inceptionDate).setMonth(inceptionDate.getMonth() + 1) > asOfDate;
    const IsLinkedPerf3Mth =
      new Date(inceptionDate).setMonth(inceptionDate.getMonth() + 3) > asOfDate;
    const IsLinkedPerf1Yr =
      new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 1) > asOfDate;
    const IsLinkedPerf3Yr =
      new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 3) > asOfDate;
    const IsLinkedPerf5Yr =
      new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 5) > asOfDate;
    const IsLinkedPerf10Yr =
      new Date(inceptionDate).setFullYear(inceptionDate.getFullYear() + 10) > asOfDate;
    if (suppressColumns) {
      showSuperscript =
        (cellIndex === 0 && IsLinkedPerf1Yr) ||
        (cellIndex === 1 && IsLinkedPerf3Yr) ||
        (cellIndex === 2 && IsLinkedPerf5Yr) ||
        (cellIndex === 3 && IsLinkedPerf10Yr);
    } else if (isRetirePlus) {
      showSuperscript =
        (cellIndex === 0 && IsLinkedPerf3Mth) ||
        (cellIndex === 2 && IsLinkedPerf1Yr) ||
        (cellIndex === 3 && IsLinkedPerf3Yr) ||
        (cellIndex === 4 && IsLinkedPerf5Yr) ||
        (cellIndex === 5 && IsLinkedPerf10Yr);
    } else {
      showSuperscript =
        (cellIndex === 0 && IsLinkedPerf1Mth) ||
        (cellIndex === 1 && IsLinkedPerf3Mth) ||
        (cellIndex === 3 && IsLinkedPerf1Yr) ||
        (cellIndex === 4 && IsLinkedPerf3Yr) ||
        (cellIndex === 5 && IsLinkedPerf5Yr) ||
        (cellIndex === 6 && IsLinkedPerf10Yr) ||
        (cellIndex === 7 &&
          (shareClass === 'Class R1' ||
            shareClass === 'Class R2' ||
            shareClass === 'Class R3' ||
            shareClass === 'Class R4'));
    }
    return showSuperscript;
  };

  const renderSuperscript = ({ fundPerformanceData }) => {
    const shareClass = investmentFacts?.data?.shareClass;
    if (
      (fundType === FUND_TYPES.variableAnnuityCref && shareClass === 'Class R3') ||
      !format.isValid(fundPerformanceData)
    ) {
      return null;
    }
    return '*';
  };

  const renderLinkedPerformance = (fundPerformanceData, cellIndex) => {
    const inceptionDate = new Date(format.oaDate(fundData.InceptionDate));
    const asOfDate = new Date(
      investmentFacts &&
        investmentFacts.isLoaded &&
        format.date(investmentFacts.data.netAssetsAsOfDate)
    ); //TIAACrefMonthlyFundPerf.perfDate, keep it consistent with legacy research
    const shareClass = investmentFacts?.data?.shareClass;
    const shouldShowSuperscript = getShowSuperscript({
      inceptionDate,
      asOfDate,
      cellIndex,
      shareClass
    });
    const rowValue = format.number(fundPerformanceData, {
      formatString: '+0.00'
    });

    return (
      <td
        data-key={`${rowValue}-${cellIndex}`}
        key={`${rowValue}-${cellIndex}`}
        {...className(
          getTextValueColorClass(fundPerformanceData),
          cellIndex !== 3 || suppressColumns || isRetirePlus
            ? 'border-dotted-left'
            : 'border-left-3'
        )}
      >
        {rowValue === '+0.00' ? '0.00' : rowValue}
        {shouldShowSuperscript && renderSuperscript({ fundPerformanceData })}
      </td>
    );
  };

  const PerformanceReturnsGrayText = ({ label, isString, value }) => {
    PerformanceReturnsGrayText.propTypes = {
      label: PropTypes.string,
      isString: PropTypes.bool,
      value: PropTypes.any
    };
    return (
      <div {...className('text-right', 'font-weight-normal', 'font-size-11', 'text-muted')}>
        {`${label}: `}
        {isString ? <span>{value}</span> : <time>{value}</time>}
      </div>
    );
  };

  const renderAsOfDate = (mStarDate, performanceDate, row) => {
    const conditionForMorningstarRow =
      row.Label.includes('Morningstar') &&
      mStarDate !== performanceDate &&
      mStarDate !== format.NO_VALUE;
    const conditionForBenchmarkRow =
      !row.VehicleType && isRetirePlus && mStarDate !== format.NO_VALUE;

    if (conditionForBenchmarkRow || conditionForMorningstarRow) {
      return <span {...className('as-of-date', 'ml-3')}>{`As of ${mStarDate}`}</span>;
    }
    return null;
  };

  return (
    <CardModule
      data-analytics-componenttype="interactive-graphic-component"
      data-analytics-id="IG_PerformanceReturns"
      data-analytics-section="component"
      {...className('print-performance-returns')}
    >
      <div {...className('d-print-none')}>
        <CardModule.Header>
          {moduleLabel}
          <CardModule.AsOfDate>{format.date(performanceAsOfDate)}</CardModule.AsOfDate>
          {!isRetirePlus && (
            <ButtonSwitch
              options={periods}
              onChange={onChangePeriod}
              selected={periodSelected}
              showOnMobile={true}
            />
          )}
        </CardModule.Header>
      </div>
      <div {...className('chart-table-scroll')}>
        <CardModule.Body>
          <div {...className('mb-5', 'd-print-none')}>
            <ChartContainer alt="performance returns chart" src={src} onResize={onResizeChart} />
          </div>
          <TableBasic {...className('category-header-row-label-table', 'fixed-left-column')}>
            <thead>
              <tr>
                <th role="presentation"></th>
                {!suppressColumns && !isRetirePlus && (
                  <th {...className('header-category', 'border-right-3')} colSpan={3}>
                    <div>Cumulative Returns</div>
                  </th>
                )}
                <th {...className('header-category')} colSpan={isRetirePlus ? 7 : 5}>
                  <div>Average Annualized Total Returns</div>
                </th>
              </tr>
              <tr>
                <th {...className('d-print-none')} role="presentation"></th>
                <th {...className('d-print-table-cell', 'd-none')}>
                  <div>Performance Returns {format.date(performanceAsOfDate)}</div>
                </th>
                {renderTableHeaders(suppressColumns, isRetirePlus)}
              </tr>
            </thead>
            <tbody>
              {supplementalData.map((item, rowIndex) => {
                let cellValues = Object.values(item.Values);

                if (!item.Values.hasOwnProperty('Since Inception')) {
                  cellValues.push(-32768);
                }
                //Some charts only display last 5 data points, need to populate the first 3 cells to keep display
                // TODO: update this logic so that it does not assume that results will included either 5 or 8 values
                // but will be resilient if other numbers of values are returned
                if (!suppressColumns) {
                  const arrayFill = ['--', '--', '--'];
                  if (cellValues.length === 5) {
                    cellValues = arrayFill.concat(cellValues);
                  }
                } else {
                  delete item.Values['1 Month'];
                  delete item.Values['3 Month'];
                  delete item.Values.YTD;
                }
                const mStarDate = format.oaDate(item.AsOfDate);
                return (
                  <tr
                    data-key={`${item.label || ''}-${rowIndex}`}
                    key={`${item.label || ''}-${rowIndex}`}
                  >
                    <th scope="row" {...className('text-left', 'fund-name')}>
                      {renderRowLabel(item, rowIndex)}
                      {parse(format.text(item.Label))}
                      {item.isMorningstarBenchmark && '^'}
                      {renderAsOfDate(mStarDate, performanceAsOfDate, item)}
                    </th>
                    {cellValues.map((value, cellIndex) => {
                      if (rowIndex === 0) {
                        return renderLinkedPerformance(value, cellIndex);
                      }
                      const rowValue = format.number(value, {
                        formatString: '+0.00'
                      });
                      return (
                        <td
                          data-key={`${rowValue || ''}-${cellIndex}`}
                          key={`${rowValue || ''}-${cellIndex}`}
                          {...className(
                            getTextValueColorClass(rowValue),
                            cellIndex !== 3 || suppressColumns || isRetirePlus
                              ? 'border-dotted-left'
                              : 'border-left-3'
                          )}
                        >
                          {rowValue === '+0.00' ? '0.00' : rowValue}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </TableBasic>
        </CardModule.Body>
      </div>
      <CardModule.Footer>
        {showExpenseRatio && (
          <PerformanceReturnsGrayText
            label={'Net Expense / Total Expense Ratio'}
            isString={true}
            value={`${format.percent(fundData.GrossExpenseRatio)} / ${format.percent(
              fundData.NetExpenseRatio
            )}`}
          />
        )}
        <PerformanceReturnsGrayText
          label={'Inception Date'}
          value={format.oaDate(fundData.InceptionDate)}
        />
        {showUnderlyingFundInceptionDate && (
          <PerformanceReturnsGrayText
            label={'Underlying Fund Inception Date'}
            value={format.date(underlyingFundInceptionDate)}
          />
        )}
        {showPortfolioInceptionDate && (
          <PerformanceReturnsGrayText
            label={'Portfolio Inception Date'}
            value={
              investmentFacts &&
              investmentFacts.isLoaded &&
              format.date(investmentFacts.data.portfolioInceptionDate)
            }
          />
        )}
        <div {...className('mt-1')}>
          <p
            {...className(
              'font-family-roboto',
              'inline-disclosure-text',
              'font-weight-normal',
              'mt-2',
              'print-font-size'
            )}
          >
            {performanceDisclosure && parse(performanceDisclosure)}
          </p>
          {isRetirePlus && (
            <p
              {...className(
                'font-family-roboto',
                'inline-disclosure-text',
                'font-weight-normal',
                'mt-2',
                'print-font-size'
              )}
            >
              {fundType === FUND_TYPES.retirePlus ? (
                <>
                  <p>{DISCLOSURES.performanceRetirePlusPart1}</p>
                  <p>
                    <strong>{DISCLOSURES.performanceRetirePlusPart2}</strong>
                  </p>
                  <p>{DISCLOSURES.performanceRetirePlusPart3}</p>
                  <p>{DISCLOSURES.performanceRetirePlusPart4}</p>
                </>
              ) : (
                <>
                  <p>{DISCLOSURES.performanceRetirePlusProPart1}</p>
                  <p>
                    <strong>{DISCLOSURES.performanceRetirePlusProPart2}</strong>
                  </p>
                  <p>{DISCLOSURES.performanceRetirePlusProPart3} </p>
                  <p>{DISCLOSURES.performanceRetirePlusProPart4}</p>
                </>
              )}
            </p>
          )}
          {showComparativeBenchmarkFootnote && <p>^{DISCLOSURES.comparativeBenchmark}</p>}
          {fundType === FUND_TYPES.mutualFund && isPropFund && (
            <p
              {...className(
                'font-family-roboto',
                'inline-disclosure-text',
                'font-weight-normal',
                'mt-2',
                'print-font-size'
              )}
            >
              {DISCLOSURES.performanceMutualFund}
            </p>
          )}
        </div>
        <Row>
          <Col {...className('text-right')}>
            <ButtonDisclosures module={'performance-returns'} />
          </Col>
        </Row>
      </CardModule.Footer>
    </CardModule>
  );
};

function renderTableHeaders(suppressColumns, isRetirePlus) {
  if (suppressColumns) {
    return (
      <Fragment>
        <th {...className('border-dotted-left')}>1Y</th>
        <th {...className('border-dotted-left')}>3Y</th>
        <th {...className('border-dotted-left')}>5Y</th>
        <th {...className('border-dotted-left')}>10Y</th>
        <th {...className('border-dotted-left')}>Since Inception</th>
      </Fragment>
    );
  }
  if (isRetirePlus) {
    return (
      <Fragment>
        <th {...className('border-dotted-left')}>3M</th>
        <th {...className('border-dotted-left')}>YTD</th>
        <th {...className('border-dotted-left')}>1Y</th>
        <th {...className('border-dotted-left')}>3Y</th>
        <th {...className('border-dotted-left')}>5Y</th>
        <th {...className('border-dotted-left')}>10Y</th>
        <th {...className('border-dotted-left')}>Since Inception</th>
      </Fragment>
    );
  }
  return (
    <Fragment>
      <th {...className('border-dotted-left')}>1M</th>
      <th {...className('border-dotted-left')}>3M</th>
      <th {...className('border-dotted-left')}>YTD</th>
      <th {...className('border-left-3')}>1Y</th>
      <th {...className('border-dotted-left')}>3Y</th>
      <th {...className('border-dotted-left')}>5Y</th>
      <th {...className('border-dotted-left')}>10Y</th>
      <th {...className('border-dotted-left', 'd-print-none')}>Since Inception</th>
      <th {...className('border-dotted-left', 'd-none', 'd-print-table-cell')}>SI</th>
    </Fragment>
  );
}

PerformanceReturns.defaultProps = {
  isAlternateInceptionDate: false,
  showBenchmarkAndAverage: true,
  isRetirePlus: false,
  suppressColumns: false,
  isPropFund: false
};

PerformanceReturns.propTypes = {
  isAlternateInceptionDate: PropTypes.bool,
  showBenchmarkAndAverage: PropTypes.bool,
  isRetirePlus: PropTypes.bool,
  suppressColumns: PropTypes.bool,
  isPropFund: PropTypes.bool
};

export default PerformanceReturns;
